
import React from 'react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import { TranslationText } from '@/components/i18n/TranslationText';
import { TranslationButton } from '@/components/i18n/TranslationButton';
import { NavigateFunction } from 'react-router-dom';

interface HeroSectionProps {
  isAuthenticated: boolean;
  navigate: NavigateFunction;
}

export function HeroSection({ isAuthenticated, navigate }: HeroSectionProps) {
  const { t } = useTranslation();
  
  return (
    <section className="py-16 md:py-24 lg:py-32">
      <div className="container px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div className="flex flex-col space-y-8 text-center md:text-left">
            <div className="space-y-4">
              <h1 className="text-4xl font-bold tracking-tighter sm:text-5xl md:text-6xl lg:text-7xl animate-fade-in">
                <TranslationText i18nKey="hero.companyTitle" />
              </h1>
              <p className="text-muted-foreground md:text-xl lg:text-2xl animate-fade-in [animation-delay:150ms] max-w-[600px]">
                <TranslationText i18nKey="hero.companySubtitle" />
              </p>
              <p className="text-muted-foreground md:text-xl lg:text-2xl animate-fade-in [animation-delay:200ms] max-w-[600px]">
                <TranslationText i18nKey="hero.pricing" />
              </p>
              
              <ul className="space-y-2 text-left animate-fade-in [animation-delay:250ms]">
                <li className="flex items-start">
                  <span className="mr-2">-</span>
                  <TranslationText i18nKey="hero.benefit1" />
                </li>
                <li className="flex items-start">
                  <span className="mr-2">-</span>
                  <TranslationText i18nKey="hero.benefit2" />
                </li>
              </ul>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start animate-fade-in [animation-delay:300ms]">
              <TranslationButton
                i18nKey="hero.getStarted"
                size="lg"
                className="h-12 px-8 text-lg bg-primary hover:bg-primary/90 shadow-lg hover:shadow-xl transition-all duration-300"
                onClick={() =>
                  navigate(isAuthenticated ? "/contracts" : "/register")
                }
              >
                <span>{isAuthenticated ? t("goToDashboard") : t("hero.getStarted")}</span>
                <ArrowRight className="ml-2 h-5 w-5" />
              </TranslationButton>
              <TranslationButton
                i18nKey="hero.learnMore"
                size="lg"
                variant="outline"
                className="h-12 px-8 text-lg"
                onClick={() => {
                  document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
                }}
              />
            </div>
          </div>
          <div className="relative animate-fade-in [animation-delay:450ms]">
            <div className="aspect-video overflow-hidden rounded-xl shadow-2xl">
              <img
                src="/lovable-uploads/c4c36f06-470c-4728-b00c-903378e40a1f.png"
                alt="Subtrackr Dashboard"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 to-transparent rounded-xl"></div>
            </div>
            <div className="absolute -bottom-6 -right-6 bg-background rounded-lg shadow-lg p-4 dark:bg-gray-800">
              <div className="text-sm font-medium flex items-center gap-2">
                <div className="h-3 w-3 rounded-full bg-green-500"></div>
                <TranslationText i18nKey="hero.neverMiss" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
